import cn from 'classnames'
import BaseModal, {
  BaseModalProps,
} from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import React, { ReactElement } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import { useTranslate } from 'src/localization/Trns'
import { useDeleteSavedView } from 'src/queries/savedViews.queries'
import { parseBackendErrors } from 'src/utils/error.utils'

import style from './DeleteViewConfirmationModal.style'

type DeleteViewConfirmationModalProps = BaseModalProps & {
  viewId?: string
  viewDisplayName: string
  context: TransactionReportType
}

export default function DeleteViewConfirmationModal(
  props: DeleteViewConfirmationModalProps,
): ReactElement {
  const {
    className = '',
    variant = 'popup',
    isOpen,
    onClose,
    viewId,
    viewDisplayName,
    context,
  } = props

  const { mutate: deleteView, isPending, error } = useDeleteSavedView(context)
  const { analytics } = useAnalyticsContext()

  const translate = useTranslate()

  if (viewId === undefined) {
    onClose()
  }

  return (
    <BaseModal
      className={cn('DeleteViewConfirmationModal', className)}
      variant={variant}
      isOpen={isOpen}
      onClose={onClose}
      css={style}
      aria-label={translate('Delete Saved View Modal')}
    >
      <div className={'flex-column gap-5'}>
        <div className={'font-h2'}>{translate('Delete Saved View')}</div>
        <div className={'font-regular'}>
          {translate('Are you sure you want to delete ${displayName}?', {
            displayName: viewDisplayName,
          })}
        </div>
        {error && (
          <div className="font-error-message">
            {parseBackendErrors(error).topLevelError}
          </div>
        )}
      </div>
      <div className="flex-row justify-end gap-6">
        <Button
          variant="no-shell"
          dropShadow={false}
          onClick={onClose}
          disabled={isPending}
        >
          {translate('Cancel')}
        </Button>
        <Button
          loading={isPending}
          onClick={() => {
            // viewId will never be undefined, its checked above
            deleteView(
              { body: { savedViewId: viewId || '' } },
              {
                onSuccess: () => {
                  analytics?.track('saved txn view', { action: 'deleted' })
                  onClose()
                },
              },
            )
          }}
        >
          {translate('Confirm')}
        </Button>
      </div>
    </BaseModal>
  )
}
